/**
 * DaVinciTranslationManager.js
 * Handles fetching OCR data and rendering translation overlays for DaVinci Viewer.
 */
import { getFirestore, doc, getDoc, collection, getDocs, query } from "firebase/firestore";
import { app } from "../../../firebase-config.js";
import { TranslationService } from "../../../services/TranslationService.js"; // Import Translation Service

const db = getFirestore(app);

export class DaVinciTranslationManager {
    constructor(viewerContainer, { seriesId, episodeId }) {
        this.container = viewerContainer; // The Viewer Root
        this.seriesId = seriesId;
        this.episodeId = episodeId;
        this.isEnabled = false;
        this.overlays = []; // Track created overlays for cleanup
        this.ocrCache = new Map(); // Cache OCR data: fileName -> data
    }

    async toggle(isEnabled) {
        this.isEnabled = isEnabled;
        if (this.isEnabled) {
            await this.applyTranslation();
        } else {
            this.clearOverlays();
        }
    }

    async applyTranslation() {
        if (!this.container) return;

        // 1. Find all Image Blocks in the Viewer
        const imageBlocks = Array.from(this.container.querySelectorAll('.dv-block[data-type="image"]'));

        if (imageBlocks.length === 0) {
            console.warn("[Translation] No images found in viewer.");
            return;
        }

        window.Modal.toast("AI 번역 실행 중...");

        // 2. Process each image
        for (const blockEl of imageBlocks) {
            const img = blockEl.querySelector('img');
            if (!img || !img.src) continue;

            const url = img.src;
            const fileName = this.extractFileNameFromUrl(url);

            if (!fileName) continue;

            // 3. Fetch Data (with Cache)
            let ocrData = this.ocrCache.get(fileName);
            if (!ocrData) {
                ocrData = await this.fetchOCRData(fileName);
                if (ocrData) this.ocrCache.set(fileName, ocrData);
            }

            // 4. Render Overlay
            if (ocrData && ocrData.anchors) {
                await this.renderOverlay(blockEl, ocrData.anchors);
            }
        }
    }

    extractFileNameFromUrl(url) {
        try {
            // Firebase Storage URL format: .../o/path%2Ffilename.webp?alt=...
            // Decoded: .../o/path/filename.webp?alt=...
            const decoded = decodeURIComponent(url);
            const path = decoded.split('?')[0]; // Remove query params
            const fullPath = path.substring(path.lastIndexOf('/') + 1); // filename (with extension currently)

            // Should match what 'ocr.trigger.js' expects.
            // ocr.trigger.js uses: refId = fileName.replace(".webp", "")
            // DaVinciService uploads as: {timestamp}.webp
            // So we strip ext.
            return fullPath.replace('.webp', '').replace('.png', '').replace('.jpg', '');
        } catch (e) {
            console.error("URL Parse error:", e);
            return null;
        }
    }

    async fetchOCRData(refId) {
        try {
            // series/{id}/episodes/{epId}/ocr_anchors/{refId}
            const docRef = doc(db, "series", this.seriesId, "episodes", this.episodeId, "ocr_anchors", refId);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                return snap.data();
            }
            return null;
        } catch (e) {
            console.error(`[Translation] Failed to load anchor for ${refId}`, e);
            return null;
        }
    }

    async renderOverlay(blockEl, anchors) {
        const wrapper = blockEl.querySelector('.dv-image-wrapper');
        if (!wrapper) return;

        // Container for overlays (if not exists)
        let overlayContainer = wrapper.querySelector('.dv-translation-layer');
        if (!overlayContainer) {
            overlayContainer = document.createElement('div');
            overlayContainer.className = "dv-translation-layer absolute inset-0 z-10"; // pointer-events handled by children
            wrapper.appendChild(overlayContainer);
            wrapper.style.position = 'relative'; // Ensure parent is relative
        }

        // Parallel Translation
        const promises = anchors.map(async (anchor) => {
            const b = anchor.box_percent;
            if (!b) return;

            const el = document.createElement('div');
            // Styles: White background, rounded, shadow, auto-fit text
            // Added pointer-events-auto to capture clicks if needed
            el.className = "absolute bg-white/95 text-black flex items-center justify-center text-center p-1 rounded-sm shadow-sm border border-gray-200 pointer-events-auto transition-opacity duration-300 opacity-0";

            // Positioning
            el.style.top = `${b.top * 100}%`;
            el.style.left = `${b.left * 100}%`;
            el.style.width = `${b.width * 100}%`;
            el.style.height = `${b.height * 100}%`;

            // Font Size Logic (Rough approximation)
            // If box is small, font must be small.
            // base font 1rem ~ 16px. 
            // We can clamp.
            el.style.fontSize = "clamp(10px, 2vw, 16px)";
            el.style.lineHeight = "1.2";
            el.style.overflow = "hidden";

            // Translation
            let textToShow = anchor.text;
            try {
                // Detect user language (or default to 'en' or browser lang)
                const targetLang = navigator.language.split('-')[0] || 'en';
                textToShow = await TranslationService.translate(anchor.text, targetLang);
            } catch (e) {
                console.warn("Translation failed, showing original", e);
            }

            el.innerText = textToShow;

            overlayContainer.appendChild(el);

            // Fade In
            setTimeout(() => el.classList.remove('opacity-0'), 50);

            this.overlays.push(el);
        });

        await Promise.all(promises);
    }

    clearOverlays() {
        // Find all layers in the container this manager manages
        if (this.container) {
            const layers = this.container.querySelectorAll('.dv-translation-layer');
            layers.forEach(l => l.remove());
        }
        this.overlays = [];
    }
}
